'use client';

import { useMemo } from 'react';
import { createTheme, ThemeOptions, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

import { useSettingsContext } from '@/components/settings';
import { useLocaleConfig } from '@/i18n';

// options
import { customShadows } from './custom-shadows';
import { createContrast } from './options/contrast';
import { createPresets } from './options/presets';
import { componentsOverrides } from './overrides';
// system
import { palette } from './palette';
import { shadows } from './shadows';
import { typography } from './typography';

type Props = {
  children: React.ReactNode;
};

export default function ThemeProvider({ children }: Props) {
  const { currentLang } = useLocaleConfig();
  const settings = useSettingsContext();

  const theme = useMemo(() => {
    const presets = createPresets(settings.themeColorPresets);
    const contrast = createContrast(settings.themeContrast, settings.themeMode);

    // Create base theme first to get computed values for component overrides
    const baseTheme = createTheme({
      palette: {
        ...palette(settings.themeMode),
        ...presets.palette,
        ...contrast.palette,
      },
      customShadows: {
        ...customShadows(settings.themeMode),
        ...presets.customShadows,
      },
      shadows: shadows(settings.themeMode),
      shape: { borderRadius: 8 },
      typography,
    } as ThemeOptions);

    // MUI v7: use createTheme with spread to avoid mutation
    return createTheme({
      ...baseTheme,
      components: {
        ...componentsOverrides(baseTheme),
        ...contrast.components,
      },
    });
  }, [settings.themeMode, settings.themeColorPresets, settings.themeContrast]);

  const themeWithLocale = useMemo(
    () => createTheme(theme, currentLang.systemValue),
    [currentLang.systemValue, theme],
  );

  return <MuiThemeProvider theme={themeWithLocale}>{children}</MuiThemeProvider>;
}
